import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getBillingPlans,
  getBillingSubscription,
  type BillingFeatureKey,
  type BillingPlan,
  type BillingPlanFeatures,
  type BillingResourceKey,
  type BillingSubscription,
  type PendingDeletion,
} from '@/api/billing';

/**
 * Resolved entitlement bundle for the current account. Mirrors the server's
 * `AccountEntitlements` shape from `entitlements.service.ts`.
 *
 * Plan reference: Section 6.2 - client gating architecture.
 */
export interface ClientEntitlements {
  loading: boolean;
  error: string | null;
  planId: number;
  effectivePlanId: number;
  features: BillingPlanFeatures | null;
  subscription: BillingSubscription | null;
  trial: {
    isTrialing: boolean;
    daysLeft: number | null;
    trialEnd: number | null;
  };
  readOnlyMode: boolean;
  pendingDeletion: PendingDeletion | null;
  /** Returns true if the boolean entitlement is granted on the effective plan. */
  hasFeature: (key: BillingFeatureKey) => boolean;
  /** Returns the numeric quota value, or 0 if loading/missing. */
  quotaFor: (key: BillingResourceKey) => number;
  refresh: () => Promise<void>;
}

const CACHE_TTL_MS = 60_000;
/** Section 6.4 offline grace: cached entitlements remain authoritative for 7 days when offline. */
const OFFLINE_GRACE_MS = 7 * 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'trove.entitlements.v1';

let cachedAt = 0;
let cachedFeatures: BillingPlanFeatures | null = null;
let cachedSubscription: BillingSubscription | null = null;
let cachedPendingDeletion: PendingDeletion | null = null;
let cachedPlans: BillingPlan[] | null = null;
let entitlementsLoadInFlight: Promise<void> | null = null;

interface PersistedEntitlements {
  cachedAt: number;
  features: BillingPlanFeatures | null;
  subscription: BillingSubscription | null;
  pendingDeletion?: PendingDeletion | null;
  plans: BillingPlan[] | null;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readPersisted(): PersistedEntitlements | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedEntitlements;
    if (typeof parsed?.cachedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePersisted(value: PersistedEntitlements): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage may be full or disabled; in-memory cache still works.
  }
}

function clearPersisted(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Hydrate in-memory cache from localStorage on module load so the first render
// is already populated when the user reloads or boots offline.
(() => {
  const persisted = readPersisted();
  if (!persisted) return;
  if (Date.now() - persisted.cachedAt > OFFLINE_GRACE_MS) {
    clearPersisted();
    return;
  }
  cachedAt = persisted.cachedAt;
  cachedFeatures = persisted.features;
  cachedSubscription = persisted.subscription;
  cachedPendingDeletion = persisted.pendingDeletion ?? null;
  cachedPlans = persisted.plans;
})();

/**
 * Hook that pulls current plan + features + subscription. 60s in-memory cache
 * (Section 6.2). Re-checks on window focus/visibility only when cache is stale;
 * use refresh() for a forced reload (e.g. billing settings after plan change).
 */
export function useEntitlements(): ClientEntitlements {
  const [loading, setLoading] = useState<boolean>(!cachedFeatures);
  const [error, setError] = useState<string | null>(null);
  const [features, setFeatures] = useState<BillingPlanFeatures | null>(cachedFeatures);
  const [subscription, setSubscription] = useState<BillingSubscription | null>(cachedSubscription);
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(cachedPendingDeletion);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async (force = false): Promise<void> => {
    if (!force && cachedFeatures && Date.now() - cachedAt < CACHE_TTL_MS) {
      setFeatures(cachedFeatures);
      setSubscription(cachedSubscription);
      setPendingDeletion(cachedPendingDeletion);
      setLoading(false);
      return;
    }
    if (!force && entitlementsLoadInFlight) {
      await entitlementsLoadInFlight;
      if (mountedRef.current) {
        setFeatures(cachedFeatures);
        setSubscription(cachedSubscription);
        setPendingDeletion(cachedPendingDeletion);
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError(null);
    const run = async (): Promise<void> => {
      const [{ subscription: sub, pendingDeletion: pending }, plans] = await Promise.all([
        getBillingSubscription(),
        cachedPlans ? Promise.resolve(cachedPlans) : getBillingPlans(),
      ]);
      cachedPlans = plans;
      const planIdToUse = sub.effectivePlanId ?? sub.planId;
      const plan = plans.find((p) => p.id === planIdToUse) ?? plans.find((p) => p.id === 0) ?? null;
      const resolvedFeatures = plan?.features ?? null;
      cachedFeatures = resolvedFeatures;
      cachedSubscription = sub;
      cachedPendingDeletion = pending;
      cachedAt = Date.now();
      writePersisted({
        cachedAt,
        features: resolvedFeatures,
        subscription: sub,
        pendingDeletion: pending,
        plans,
      });
      if (!mountedRef.current) return;
      setFeatures(resolvedFeatures);
      setSubscription(sub);
      setPendingDeletion(pending);
    };
    const promise = run()
      .catch((err) => {
        // Section 6.4 offline grace: if a recent cache (< 7d) exists, treat it
        // as authoritative; otherwise surface the error and fall through to
        // Free-tier defaults. Either way, we never throw to the caller.
        const message = err instanceof Error ? err.message : String(err);
        const haveCache =
          cachedFeatures !== null && Date.now() - cachedAt < OFFLINE_GRACE_MS;
        if (!mountedRef.current) return;
        if (haveCache) {
          setFeatures(cachedFeatures);
          setSubscription(cachedSubscription);
          setPendingDeletion(cachedPendingDeletion);
          setError(null);
        } else {
          setError(message);
        }
      })
      .finally(() => {
        if (entitlementsLoadInFlight === promise) entitlementsLoadInFlight = null;
        if (mountedRef.current) setLoading(false);
      });
    entitlementsLoadInFlight = promise;
    await promise;
  }, []);

  useEffect(() => {
    void load(false);
    const onFocus = (): void => {
      if (document.visibilityState !== 'visible') return;
      // Respect 60s cache on routine focus/visibility — avoids billing GET on
      // every tab close / shell refocus. Call refresh() for forced reload.
      void load(false);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const hasFeature = useCallback(
    (key: BillingFeatureKey): boolean => Boolean(features?.[key] === true),
    [features]
  );

  const quotaFor = useCallback(
    (key: BillingResourceKey): number => {
      const v = features?.[key];
      return typeof v === 'number' ? v : 0;
    },
    [features]
  );

  const trial = useMemo(() => {
    const isTrialing = subscription?.status === 'trialing';
    const trialEnd = subscription?.trialEnd ?? null;
    const daysLeft =
      isTrialing && typeof trialEnd === 'number'
        ? Math.max(0, Math.ceil((trialEnd - Date.now()) / (24 * 60 * 60 * 1000)))
        : null;
    return { isTrialing, daysLeft, trialEnd };
  }, [subscription?.status, subscription?.trialEnd]);

  const refresh = useCallback(async (): Promise<void> => {
    await load(true);
  }, [load]);

  return {
    loading,
    error,
    planId: subscription?.planId ?? 0,
    effectivePlanId: subscription?.effectivePlanId ?? subscription?.planId ?? 0,
    features,
    subscription,
    pendingDeletion,
    trial,
    readOnlyMode: Boolean(subscription?.readOnlyMode) || pendingDeletion != null,
    hasFeature,
    quotaFor,
    refresh,
  };
}

/** Test-only: clears both the in-memory and the localStorage caches. */
export function _resetEntitlementsCacheForTests(): void {
  cachedAt = 0;
  cachedFeatures = null;
  cachedSubscription = null;
  cachedPendingDeletion = null;
  cachedPlans = null;
  entitlementsLoadInFlight = null;
  clearPersisted();
}
